import { gql, useMutation } from '@apollo/client';
import { Pda } from './components/Pda';
import { Header } from './components/Header';
import { isBagNumber } from '../../utils/BarCodeUtil';
import { Switch, Popup } from "antd-mobile";
import { ArrowLeftOutlined, SettingOutlined } from "@ant-design/icons";
import React, { useState } from "react";
import { t } from '../../i18n';
import { getNavBarContainerStyle } from '../../utils/deviceUtils';
import BluetoothScaleSelector from './components/BluetoothScaleSelector';
import { useBluetoothScaleStore } from '../../store/bluetoothScaleStore';
import { ArrivalScanWightComponent } from "./components/ArrivalScanWeightComponent";
import { ScanResultEnvelope, ScanResultPrompt, useScanResultPrompt, ClientError, buildClientError } from './scanResult';
import type { ScanPromptInfo } from './scanResult';

// 与后端 ArrivalScanResponse 对齐：mutation 直接返回 ArrivalScanResponse，无外壳包裹
interface ArrivalScanResponseGql {
    success: boolean;
    messageKeyStr?: string | null;
    message?: string | null;
    promptInfo?: ScanPromptInfo | null;
}

interface HandleArrivalScanEventResult {
    handleArrivalScanEvent: ArrivalScanResponseGql;
}

const HANDLE_ARRIVAL_SCAN_EVENT = gql`
    mutation HandleArrivalScanEvent($barCode: String!, $weightKg: Float) {
        handleArrivalScanEvent(barCode: $barCode, weightKg: $weightKg) {
            success
            message
            messageKeyStr
            promptInfo {
                messageKey
                popupType
                title
                content
            }
        }
    }
`

interface PageBodyProps {
    showSettings: boolean;
    setShowSettings: (show: boolean) => void;
    scanPrompt: ReturnType<typeof useScanResultPrompt>;
}

const PageBody = ({ showSettings, setShowSettings, scanPrompt }: PageBodyProps) => {
    const [handleArrivalScanEvent] = useMutation<HandleArrivalScanEventResult>(HANDLE_ARRIVAL_SCAN_EVENT);

    const { isConnected: isBluetoothConnected, currentWeightData, weighSimultaneously, setWeighSimultaneously } = useBluetoothScaleStore();

    const handleScanEvent = async (barCode: string): Promise<ScanResultEnvelope<unknown>> => {
        let newWeighSimultaneously = weighSimultaneously;
        let weightKg: number | undefined = undefined;
        // 当前为包牌，不做重量处理
        if (isBagNumber(barCode)) {
            newWeighSimultaneously = false;
            weightKg = undefined;
        }
        if (newWeighSimultaneously) {
            if (!isBluetoothConnected) {
                return buildClientError(ClientError.BLUETOOTH_NOT_CONNECTED);
            }
            if (!currentWeightData || currentWeightData.weight <= 0) {
                return buildClientError(ClientError.WEIGHT_NOT_AVAILABLE);
            }
            if (currentWeightData.rawData && currentWeightData.rawData.charAt(0) === '-') {
                return buildClientError(ClientError.NEGATIVE_WEIGHT);
            }
            // 单位转换：lb 转 kg，kg 保持不变
            weightKg = currentWeightData.unit.toLowerCase() === 'lb'
                ? currentWeightData.weight * 0.453592
                : currentWeightData.weight;
            weightKg = Math.round(weightKg * 1000) / 1000;
        }

        const { data } = await handleArrivalScanEvent({
            variables: {
                barCode,
                weightKg: weightKg
            }
        });

        const result = data?.handleArrivalScanEvent;
        // mutation 直接返回 ArrivalScanResponse（无 data 外壳），
        // result 本身带 promptInfo，透传给 ScanResultEnvelope.data，hook 从 data.promptInfo 读取
        return {
            success: result?.success ?? false,
            message: result?.message ?? null,
            data: result ?? null,
        };
    };

    return (
        <>
            {/* 设置弹出层 */}
            <Popup
                visible={showSettings}
                onMaskClick={() => setShowSettings(false)}
                position="right"
                bodyStyle={{ width: '100vw', height: '100vh', padding: 0 }}
            >
                <div className="w-full h-full flex flex-col">
                    {/* 设置标题栏 */}
                    <div className="flex items-center px-4 py-3 bg-white shadow-sm">
                        <div
                            className="cursor-pointer"
                            onClick={() => setShowSettings(false)}
                        >
                            <ArrowLeftOutlined className="text-xl" />
                        </div>
                        <h2 className="flex-1 text-center text-lg font-semibold">{t('pda.pages.arrival.settings')}</h2>
                        <div className="w-6"></div>
                    </div>

                    {/* 设置内容 */}
                    <div className="flex-1 overflow-y-auto p-2 flex flex-col gap-2">
                        {/* 同时称重开关 */}
                        <div className="bg-white p-4 rounded">
                            <div className="flex justify-between items-center">
                                <span className="text-base font-medium">{t('pda.pages.arrival.weighSimultaneously')}</span>
                                <Switch
                                    checked={weighSimultaneously}
                                    onChange={setWeighSimultaneously}
                                />
                            </div>
                        </div>

                        {/* 蓝牙电子秤组件 */}
                        <BluetoothScaleSelector />
                    </div>
                </div>
            </Popup>

            {/* 扫描组件 */}
            <ArrivalScanWightComponent
                value1Placeholder={t('pda.pages.arrival.bagCodeTrackingNo')}
                value1ColumnTitle={t('pda.pages.arrival.bagCodeTrackingNo')}
                handleScanEvent={handleScanEvent}
                scanPrompt={scanPrompt}
                weighSimultaneously={weighSimultaneously}
                currentWeight={currentWeightData}
                isBluetoothConnected={isBluetoothConnected}
                successExtraFields={weighSimultaneously ? [{ title: t('pda.pages.arrival.weight'), key: 'weight' }] : undefined}
            />
        </>
    );
}

export default function ArrivalPage() {
    const [showSettings, setShowSettings] = useState(false);
    const scanPrompt = useScanResultPrompt();

    return (
        <div className="w-full h-full relative">
            {/* 扫描结果反馈层（绿框 / 红框 / 橙框 / 黑底 NOTICE，按 popupType 自动渲染） */}
            <ScanResultPrompt
                prompt={scanPrompt.prompt}
                barCode={scanPrompt.barCode}
                onClose={scanPrompt.clearPrompt}
            />

            {/* 内容区域 */}
            <div className="w-full h-full flex flex-col gap-2 p-2">
                {/* 顶部导航栏 */}
                <div
                    {...getNavBarContainerStyle('relative')}
                    style={{ ...(getNavBarContainerStyle().style || {}), paddingTop: 0 }}
                >
                    <Header title={t('pda.pages.arrival.arrivalScan')}/>
                    <div
                        className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center cursor-pointer"
                        onClick={() => setShowSettings(true)}
                    >
                        <SettingOutlined className="text-xl text-gray-600" />
                    </div>
                </div>
                <Pda/>
                <PageBody
                    showSettings={showSettings}
                    setShowSettings={setShowSettings}
                    scanPrompt={scanPrompt}
                />
            </div>
        </div>
    );
}